import React, { useState, useEffect } from 'react';
import { Calculator, ArrowRight, Check, Zap } from 'lucide-react';
import { aimflexData } from '../../data/aimflexData';
import { contactWhatsApp } from '../../utils/buttonActions';
import { submitNetlifyForm } from '../../utils/netlifyForms';
import SectionHeader from '../SectionHeader';
import IndustrialButton from '../IndustrialButton';

const SmartQuote = () => {
  const [formData, setFormData] = useState({
    category: '',
    material: '',
    width: '',
    height: '',
    addOns: [],
    customerName: '',
    customerPhone: '',
    customerEmail: ''
  });

  const [calculation, setCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteRef, setQuoteRef] = useState('');
  const [quoteError, setQuoteError] = useState('');

  useEffect(() => {
    const width = parseFloat(formData.width);
    const height = parseFloat(formData.height);

    if (formData.category && formData.material && width > 0 && height > 0) {
      setIsCalculating(true);
      const timer = setTimeout(() => {
        const result = aimflexData.quoteCalculator.calculatePrice(
          formData.category,
          formData.material,
          width,
          height,
          formData.addOns
        );
        setCalculation(result);
        setIsCalculating(false);
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setCalculation(null);
    }
  }, [formData.category, formData.material, formData.width, formData.height, formData.addOns]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setQuoteError('');
  };

  const handleAddOnToggle = (addOnId) => {
    setFormData(prev => ({
      ...prev,
      addOns: prev.addOns.includes(addOnId)
        ? prev.addOns.filter(id => id !== addOnId)
        : [...prev.addOns, addOnId]
    }));
  };

  const getSelectedMaterial = () => {
    const category = aimflexData.productCategories.find(cat =>
      cat.name.toLowerCase().replace(' ', '') === formData.category
    );
    return category?.materials.find(mat => mat.id === formData.material);
  };

  const getSelectedAddOnNames = () =>
    formData.addOns
      .map((addOnId) => aimflexData.addOns.find((item) => item.id === addOnId)?.name)
      .filter(Boolean);

  const handleSubmitQuote = async () => {
    const width = parseFloat(formData.width);
    const height = parseFloat(formData.height);

    if (!calculation || width <= 0 || height <= 0) {
      setQuoteError('Enter valid positive dimensions before requesting a quote.');
      return;
    }

    if (!formData.customerName.trim() || !formData.customerPhone.trim()) {
      setQuoteError('Enter your name and phone number to get the final quote.');
      return;
    }

    setIsSubmitting(true);
    setQuoteError('');
    try {
      const selectedMat = getSelectedMaterial();
      const selectedAddOnNames = getSelectedAddOnNames();
      const payload = {
        category: formData.category,
        material: formData.material,
        material_name: selectedMat?.name || '',
        width,
        height,
        add_ons: formData.addOns,
        customer_name: formData.customerName,
        customer_phone: formData.customerPhone,
        customer_email: formData.customerEmail
      };

      const quoteRefValue = `AIM-${Date.now().toString().slice(-8)}`;

      await submitNetlifyForm('quote', {
        ...payload,
        area: calculation.area,
        base_price: calculation.basePrice,
        add_on_cost: calculation.addOnCost,
        total_price: calculation.totalPrice,
        add_ons: selectedAddOnNames
      });

      setQuoteRef(quoteRefValue);
      setSubmitted(true);

      const message = `Aim Flex Smart Quote
Quote Ref: ${quoteRefValue}
Customer: ${formData.customerName}
Phone: ${formData.customerPhone}
Email: ${formData.customerEmail || 'Not provided'}
Category: ${formData.category.toUpperCase()}
Material: ${selectedMat?.name}
Dimensions: ${formData.width} x ${formData.height} ft
Area: ${calculation?.area} sq ft
Material Cost: Rs.${calculation?.basePrice?.toLocaleString()}
Add-ons: ${selectedAddOnNames.length ? selectedAddOnNames.join(', ') : 'None'}
Add-on Cost: Rs.${calculation?.addOnCost?.toLocaleString()}
TOTAL: Rs.${calculation?.totalPrice?.toLocaleString()}`;

      contactWhatsApp(message);
      setFormData({
        category: '',
        material: '',
        width: '',
        height: '',
        addOns: [],
        customerName: '',
        customerPhone: '',
        customerEmail: ''
      });
    } catch (error) {
      console.error('Quote submission error:', error);
      setQuoteError('Unable to generate your quote right now. Please call or WhatsApp us.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableAddOns = aimflexData.addOns.filter(addOn =>
    ['lamination-matte', 'lamination-gloss', 'grommets', 'mounting'].includes(addOn.id)
  );

  return (
    <section id="smart-quote" className="py-20 lg:py-28 bg-[#0A0A0A] text-white border-b-2 border-[#0A0A0A]" data-testid="smart-quote-section">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeader
          eyebrow="Pricing Engine"
          title="Smart"
          accent="Quote"
          copy="Configure material, size, finishing, and contact details in one technical quote flow."
        />

        {submitted ? (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white text-[#0A0A0A] border-2 border-white shadow-[8px_8px_0px_#FF2A00] p-8 lg:p-12 text-center" data-testid="quote-success">
              <div className="w-16 h-16 bg-[#0A0A0A] flex items-center justify-center mx-auto mb-6">
                <Check className="h-8 w-8 text-[#FF2A00]" strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-black tracking-tighter uppercase text-[#0A0A0A] mb-2">QUOTE GENERATED</h3>
              <p className="text-xs font-bold tracking-widest text-[#999] mb-6">REF: {quoteRef}</p>
              <div className="bg-[#F9F9F6] border-2 border-[#0A0A0A] p-6">
                <div className="text-4xl font-black text-[#FF2A00] tracking-tighter">
                  Rs.{calculation?.totalPrice?.toLocaleString()}
                </div>
                <div className="text-sm text-[#666] mt-1">{calculation?.area} sq ft project</div>
              </div>
              <p className="text-[#404040] mt-6">Sent to WhatsApp. Our team will contact you within 2 hours.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-reveal>
            {/* Quote Form */}
            <div className="xl:col-span-2">
              <div className="bg-white text-[#0A0A0A] border-2 border-white shadow-[8px_8px_0px_#FF2A00]">
                <div className="bg-[#0A0A0A] text-white p-6 flex items-center space-x-3">
                  <Calculator className="h-5 w-5 text-[#FF2A00]" strokeWidth={2} />
                  <span className="text-sm font-bold tracking-widest uppercase">Configure Your Project</span>
                </div>

                <div className="p-6 lg:p-8 space-y-8">
                  {/* Step 1: Category */}
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#0A0A0A] mb-4">
                      01 - SELECT CATEGORY
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aimflexData.productCategories.map((category) => (
                        <button
                          type="button"
                          key={category.id}
                          onClick={() => {
                            handleInputChange('category', category.name.toLowerCase().replace(' ', ''));
                            handleInputChange('material', '');
                          }}
                          className={`p-5 border-2 text-left transition-all duration-200 ${
                            formData.category === category.name.toLowerCase().replace(' ', '')
                              ? 'border-[#FF2A00] bg-[#FF2A00] text-white shadow-brutal-sm'
                              : 'border-[#0A0A0A] hover:border-[#FF2A00] bg-white'
                          }`}
                          data-testid={`category-${category.id}`}
                        >
                          <div className="text-base font-bold tracking-tight uppercase">{category.name}</div>
                          <div className={`text-lg font-black tracking-tighter mt-1 ${
                            formData.category === category.name.toLowerCase().replace(' ', '') ? 'text-white' : 'text-[#FF2A00]'
                          }`}>
                            {category.priceRange}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Material */}
                  {formData.category && (
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#0A0A0A] mb-4">
                        02 - CHOOSE MATERIAL
                      </label>
                      <div className="space-y-3">
                        {aimflexData.productCategories
                          .find(cat => cat.name.toLowerCase().replace(' ', '') === formData.category)
                          ?.materials.map((material) => (
                            <button
                              type="button"
                              key={material.id}
                              onClick={() => handleInputChange('material', material.id)}
                              className={`w-full p-4 border-2 text-left transition-all duration-200 flex justify-between items-center ${
                                formData.material === material.id
                                  ? 'border-[#FF2A00] bg-[#FFF5F3]'
                                  : 'border-[#0A0A0A] hover:border-[#FF2A00] bg-white'
                              }`}
                              data-testid={`material-${material.id}`}
                            >
                              <div>
                                <div className="text-base font-bold text-[#0A0A0A]">{material.name}</div>
                                <div className="text-xs text-[#666] mt-1">{material.bestFor}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-black text-[#FF2A00]">Rs.{material.price}</div>
                                <div className="text-xs text-[#999]">per sq ft</div>
                              </div>
                            </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Dimensions */}
                  {formData.material && (
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#0A0A0A] mb-4">
                        03 - DIMENSIONS
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold tracking-widest text-[#666] mb-2">WIDTH (FT)</label>
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            required
                            placeholder="10"
                            value={formData.width}
                            onChange={(e) => handleInputChange('width', e.target.value)}
                            className="w-full p-3 border-2 border-[#0A0A0A] bg-white text-xl font-bold text-center focus:border-[#FF2A00] focus:outline-none transition-colors"
                            data-testid="input-width"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold tracking-widest text-[#666] mb-2">HEIGHT (FT)</label>
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            required
                            placeholder="6"
                            value={formData.height}
                            onChange={(e) => handleInputChange('height', e.target.value)}
                            className="w-full p-3 border-2 border-[#0A0A0A] bg-white text-xl font-bold text-center focus:border-[#FF2A00] focus:outline-none transition-colors"
                            data-testid="input-height"
                          />
                        </div>
                      </div>
                      {formData.width && formData.height && (
                        <div className="mt-4 bg-[#0A0A0A] text-white p-4 text-center">
                          <span className="text-xs font-bold tracking-widest">TOTAL AREA:</span>
                          <span className="text-2xl font-black text-[#FF2A00] ml-3">
                            {(parseFloat(formData.width) * parseFloat(formData.height) || 0).toFixed(1)} SQ FT
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: Add-ons */}
                  {calculation && (
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#0A0A0A] mb-4">
                        04 - ADD-ONS (OPTIONAL)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {availableAddOns.map((addOn) => (
                          <button
                            type="button"
                            key={addOn.id}
                            onClick={() => handleAddOnToggle(addOn.id)}
                            className={`p-4 border-2 text-left transition-all duration-200 ${
                              formData.addOns.includes(addOn.id)
                                ? 'border-[#FF2A00] bg-[#FFF5F3]'
                                : 'border-[#0A0A0A] hover:border-[#FF2A00] bg-white'
                            }`}
                            data-testid={`addon-${addOn.id}`}
                          >
                            <div className="flex justify-between">
                              <span className="text-sm font-bold text-[#0A0A0A]">{addOn.name}</span>
                              <span className="text-sm font-black text-[#FF2A00]">Rs.{addOn.price}/{addOn.unit.replace('per ', '')}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 5: Customer Info */}
                  {calculation && (
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#0A0A0A] mb-4">
                        05 - YOUR DETAILS
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          placeholder="Your Name *"
                          required
                          value={formData.customerName}
                          onChange={(e) => handleInputChange('customerName', e.target.value)}
                          className="p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="input-name"
                        />
                        <input
                          placeholder="Phone Number *"
                          required
                          value={formData.customerPhone}
                          onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                          className="p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="input-phone"
                        />
                        <input
                          placeholder="Email (Optional)"
                          type="email"
                          value={formData.customerEmail}
                          onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                          className="p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="input-email"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live Quote Summary */}
            <div>
              <div className="bg-white text-[#0A0A0A] border-2 border-white shadow-[8px_8px_0px_#FF2A00] sticky top-24 quote-pulse-panel" data-testid="live-quote-panel">
                <div className="bg-[#FF2A00] text-white p-6 flex items-center space-x-3">
                  <Zap className="h-5 w-5" strokeWidth={2} />
                  <span className="text-sm font-bold tracking-widest uppercase">LIVE QUOTE</span>
                </div>

                <div className="p-6 space-y-6">
                  {isCalculating ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-10 h-10 border-2 border-[#0A0A0A] border-t-[#FF2A00] mx-auto mb-4"></div>
                      <div className="text-sm font-bold tracking-widest uppercase">CALCULATING...</div>
                    </div>
                  ) : calculation ? (
                    <>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666]">Material</span>
                          <span className="font-bold text-[#0A0A0A]">{getSelectedMaterial()?.name}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666]">Area</span>
                          <span className="font-bold text-[#0A0A0A]">{calculation.area} sq ft</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666]">Material Cost</span>
                          <span className="font-bold text-[#0A0A0A]">Rs.{calculation.basePrice.toLocaleString()}</span>
                        </div>
                        {calculation.addOnCost > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#666]">Add-ons</span>
                            <span className="font-bold text-[#0A0A0A]">Rs.{calculation.addOnCost.toLocaleString()}</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t-2 border-[#0A0A0A] pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-black tracking-tighter uppercase">TOTAL</span>
                          <span className="text-3xl font-black text-[#FF2A00] tracking-tighter">
                            Rs.{calculation.totalPrice.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {formData.customerName && formData.customerPhone && (
                        <IndustrialButton
                          onClick={handleSubmitQuote}
                          disabled={isSubmitting}
                          className="w-full justify-center"
                          testId="quote-submit-btn"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent mr-2"></div>
                              GENERATING...
                            </>
                          ) : (
                            <>
                              GET FINAL QUOTE
                              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                            </>
                          )}
                        </IndustrialButton>
                      )}
                      {quoteError && (
                        <p className="text-sm font-medium text-[#FF2A00] text-center" role="alert">
                          {quoteError}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Calculator className="h-12 w-12 mx-auto mb-4 text-[#ccc]" strokeWidth={1.5} />
                      <div className="text-sm font-bold tracking-widest uppercase text-[#0A0A0A]">CONFIGURE PROJECT</div>
                      <div className="text-xs text-[#999] mt-1">Fill in details to see live pricing</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SmartQuote;
